import { Component, Input, Output, EventEmitter, forwardRef, OnInit } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

@Component({
  selector: 'app-no-highlight-autocomplete',
  templateUrl: './no-highlight-autocomplete.component.html',
  styleUrls: ['./no-highlight-autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NoHighlightAutocompleteComponent),
      multi: true
    }
  ]
})
export class NoHighlightAutocompleteComponent implements ControlValueAccessor, OnInit {
  @Input() placeholder = '';
  @Input() suggestions: string[] = [];
  @Input() required = false;
  @Input() invalidFeedback = '';
  @Input() isInvalid = false;
  @Input() inputClass = 'form-control';
  @Input() name = '';
  @Input() label = '';
  @Input() showLabel = true;

  @Output() selectedOption = new EventEmitter<string>();

  // Internal state
  value = '';
  filteredSuggestions: string[] = [];
  disabled = false;
  touched = false;

  // ControlValueAccessor methods
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  ngOnInit() {
    this.resetSuggestions();
  }

  /**
   * Reset suggestions to initial state
   */
  resetSuggestions() {
    this.filteredSuggestions = [...this.suggestions];
  }

  /**
   * Filter suggestions based on input
   */
  filterSuggestions(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();
    this.filteredSuggestions = this.suggestions.filter(suggestion =>
      suggestion.toLowerCase().includes(filterValue)
    );

    // Update model value with current input
    this.value = (event.target as HTMLInputElement).value;
    this.onChange(this.value);

    // Mark as touched on input
    this.onTouched();
  }

  /**
   * Handle option selection from autocomplete and remove highlighting
   * @param event The autocomplete selection event
   */
  handleOptionSelected(event: MatAutocompleteSelectedEvent): void {
    // Get selected value
    const selectedValue = event.option.value;

    // Update internal value and notify
    this.value = selectedValue;
    this.onChange(selectedValue);
    this.selectedOption.emit(selectedValue);

    // Using the strategy that redrawing options removes highlighting
    this.filteredSuggestions = [];
    setTimeout(() => {
      this.filteredSuggestions = [...this.suggestions];
    });
  }

  /**
   * Display function for autocomplete to prevent highlighting
   */
  displayFn(value: string): string {
    return value || '';
  }

  // ControlValueAccessor interface implementation
  writeValue(value: string | null | undefined): void {
    this.value = value || '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Helper to mark as touched
  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;

      // Ensure the current value is properly synchronized with the model
      this.onChange(this.value);
    }
  }
}
